/**
 * ROW 391 (William, 18 Sep 2026): the Inbound queue's reply groups and the three presses, in ONE
 * place for the screen. The server decides for real in api/src/lib/reply-actions.ts; OP_CLASSES below
 * mirrors its table and a lockstep test fails the build if the two ever disagree, so a button can
 * never offer a press the server would refuse by class.
 *
 * Shown only when the deployment says features.replyButtons (MEMBERCARE_REPLY_BUTTONS).
 */
import type { AnswerChangedFlag, EventRow, LaterRefusalFlag, ReplyNoteFlag, ReplyOffer, SmsReplyClass } from '@lib/care-model';

/** The four groups on top of the flat queue: his "Opt In", "Opt Out", "Help" and "all other". */
export type ReplyGroup = 'optin' | 'optout' | 'help' | 'other';

export const REPLY_GROUPS: ReadonlyArray<{ key: ReplyGroup; label: string; help: string }> = [
  { key: 'optin', label: 'Opt In', help: 'Every text reply that said 1 (yes) to a kit offer.' },
  { key: 'optout', label: 'Opt Out', help: 'Every text reply that said 2: no to THIS campaign only.' },
  { key: 'help', label: 'Help', help: 'Every text reply that said HELP.' },
  { key: 'other', label: 'All other', help: 'Every other text reply: questions and anything else, and STOP (marked in red).' },
];

/**
 * The group a row belongs to, or null for a row that is not a text reply. ALL OTHER is every reply
 * that is not a 1, a 2 or a HELP, STOP included; an email, a call or a referral is in no group, so
 * choosing a group shows replies only, and choosing none shows the whole queue exactly as before.
 */
export function replyGroupOf(cls: SmsReplyClass | '' | undefined): ReplyGroup | null {
  if (!cls) return null;
  if (cls === 'YES_KIT') return 'optin';
  if (cls === 'DECLINE_CAMPAIGN') return 'optout';
  if (cls === 'HELP') return 'help';
  return 'other';
}

/**
 * APPROVAL 185 (William, 18 Sep 2026: "We need to match up each opt-in with the kit type we need to
 * send out"): the offer a reply answered, in her words, for the Offer column and its slicer. An offer
 * this table does not name reads as blank, never as a known one.
 *
 * The OFFER is not the kit: the KED offer covers uACR (urine only), KED (urine and blood) and eGFR
 * (blood only), and the member's COHORT decides which. The cohort-to-kit mapping is not yet confirmed
 * by Printing and the Warehouse, so the Cohort column shows the roster's own words and this file names
 * no kit.
 */
export const REPLY_OFFER_WORDING: Readonly<Record<ReplyOffer, string>> = {
  a1c: 'A1c only',
  ked: 'KED only',
  a1cked: 'A1c and KED',
};

/** The Offer column's words for a row's `replyOffer`; '' for a blank or an offer this table does not name. */
export function replyOfferLabel(v: string | null | undefined): string {
  const k = String(v ?? '');
  return Object.prototype.hasOwnProperty.call(REPLY_OFFER_WORDING, k) ? REPLY_OFFER_WORDING[k as ReplyOffer] : '';
}

/**
 * APPROVAL 188: the group a ROW is shown in. The server reads the member's whole reply history and
 * names it (`replyGroupHint`: the member's LAST answer per campaign in Opt In or Opt Out, every other
 * reply of theirs in All other); a row it did not name (the switch off) falls back to its class.
 */
export function rowReplyGroup(row: Pick<EventRow, 'replyClass' | 'replyGroupHint'>): ReplyGroup | null {
  return row.replyGroupHint || replyGroupOf(row.replyClass);
}

/** The four presses the server takes; `refer` (row 400 rev2, Send to Main Dock) is the 01A grid's own. */
export type ReplyOp = 'opt-in' | 'opt-out' | 'stop' | 'refer';
/** The DRAWER's entry presses, in its order. The 01A grid offers opt-in and refer on its own pills. */
export const REPLY_OPS: readonly ReplyOp[] = ['opt-in', 'opt-out', 'stop'];

/** MIRROR of api/src/lib/reply-actions.ts OP_CLASSES (pinned by tests/unit/reply-groups.test.ts). */
export const OP_CLASSES: Readonly<Record<ReplyOp, readonly SmsReplyClass[]>> = {
  'opt-in': ['YES_KIT', 'HELP', 'OTHER'],
  'opt-out': ['DECLINE_CAMPAIGN', 'HELP', 'OTHER'],
  stop: ['STOP'],
  refer: ['YES_KIT', 'HELP', 'OTHER'],
};

/** The words on each press, in one table. */
export const REPLY_OP_WORDING: Readonly<Record<ReplyOp, { button: string; move: string; help: string; done: string }>> = {
  'opt-in': {
    button: 'Opt In',
    move: 'Move to opted in',
    help: 'Approve the kit: the approval goes to the kit-requests record for the Portal and Printing, and the entry moves to your Archive as approved.',
    done: 'approved and archived',
  },
  'opt-out': {
    button: 'Opt Out',
    move: 'Move to opted out',
    help: 'Opt the member out of THIS campaign only: the member\'s file and the campaign\'s opt-out list, then the entry moves to your Archive as resolved.',
    done: 'opted out of the campaign and archived',
  },
  stop: {
    button: 'Move to do not contact',
    move: 'Move to do not contact',
    help: 'The member texted STOP. Writes do not contact on the member\'s file (the texting platform listed the number when the STOP arrived), then the entry moves to your Archive as resolved.',
    done: 'moved to do not contact and archived',
  },
  // Row 400 rev2: his yellow pill on the 01A grid. No kit is approved; a person in the main queue looks.
  refer: {
    button: 'Send to Main Dock',
    move: 'Send to Main Dock',
    help: 'Send the member to the main Inbound queue for review: an under-review line goes to the kit-requests record for the Portal, a new entry is made in the main queue naming who sent it and the member\'s words, and this entry moves to your Archive. No kit is approved.',
    done: 'sent to the main queue and archived',
  },
};

/** The row fields a press reads (row 391's, and the reply history of approvals 187 and 188). */
export type PressRow = Pick<EventRow, 'eventId' | 'replyClass' | 'memberId' | 'laterRefusal' | 'replyAnswer' | 'replyNote'>;

/**
 * Whether a row can take a press: its reply class fits, and it is matched to a member. On top, the
 * refusals the server would name, so a count never offers a row the press would refuse, in ANY group:
 *   Opt In   a row the member refused later (187), a reaction, a reply that is not the member's last
 *            answer (both carry a note), and a last answer of no (188).
 *   Opt Out  a reply that is not the member's last answer (its note names that answer), and a last
 *            answer of yes (188).
 * A Read first row still fits: her single tick after reading it is her decision.
 */
export function opFits(op: ReplyOp, row: Omit<PressRow, 'eventId'>): boolean {
  const cls = row.replyClass;
  if (op === 'opt-in' && (row.laterRefusal || row.replyNote || (row.replyAnswer === 'no' && !row.replyNote))) return false;
  if (op === 'opt-out' && (row.replyNote?.eventId || (row.replyAnswer === 'yes' && !row.replyNote))) return false;
  // Row 400 rev2: a referral keeps the 188 refusals (a note, a last answer of no) and DROPS the 187
  // one: a member who said STOP later can still be sent for a person to review; the flag rides along.
  if (op === 'refer' && (row.replyNote || (row.replyAnswer === 'no' && !row.replyNote))) return false;
  return !!cls && OP_CLASSES[op].includes(cls) && !!row.memberId;
}

/** APPROVAL 188: whether Select all may take a row. A Read first row is ticked one by one, never in bulk. */
export function selectAllTakes(row: Pick<EventRow, 'replyReadFirst'>): boolean {
  return !row.replyReadFirst;
}

/**
 * The ids a press acts on: the rows ON SCREEN (the group, the page's chips and the slicers already
 * applied), ticked, and fitting the press. A row a filter hid is never sent, however it was ticked.
 */
export function idsForOp(op: ReplyOp, visible: ReadonlyArray<PressRow>, checked: ReadonlySet<string>): string[] {
  return visible.filter((r) => checked.has(r.eventId) && opFits(op, r)).map((r) => r.eventId);
}

/** The server takes at most this many entries a press; a larger selection goes in presses of this size. */
export const MAX_PRESS = 50;

export function inPresses(ids: readonly string[], size: number = MAX_PRESS): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

/* ------------------------------ APPROVAL 187: what the member said LATER ------------------------------ */

/** A time as the Pacific clock reads it ("1:54 PM"), whatever the browser's own zone. */
export function fmtClockPt(iso: string): string {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return 'an unknown time';
  return new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', hour: 'numeric', minute: '2-digit' }).format(d);
}

/** The blocking reason in plain words: "Said STOP at 1:54 PM", "Said 2 at 1:11 PM", "On the do-not-text list". */
export function laterRefusalWords(r: LaterRefusalFlag): string {
  if (r.reason === 'do_not_text') return 'On the do-not-text list';
  return `Said ${r.reason === 'later_stop' ? 'STOP' : '2'} at ${fmtClockPt(r.at)}`;
}

/** The soft note in plain words. It never blocks a tick or a press. */
export function answerChangedWords(a: AnswerChangedFlag): string {
  return `Changed answer: said 2 at ${fmtClockPt(a.declinedAt)}, then 1. Call first.`;
}

/**
 * Why a row's tick box is disabled, '' when it is not. Only in the Opt In group, whose one press is
 * Opt In: there a refused row can never be ticked, so Select all never ticks it. In Help and All
 * other the tick stays live for Opt Out and STOP, and opFits keeps Opt In off the row instead.
 */
export function tickBlockReason(group: ReplyGroup | null, row: Pick<EventRow, 'laterRefusal'>): string {
  if (group !== 'optin' || !row.laterRefusal) return '';
  return `${laterRefusalWords(row.laterRefusal)}. Opt In refuses this entry, so it cannot be ticked here.`;
}

/** APPROVAL 188: why a reply sits outside its answer's group, in plain words. */
export function replyNoteWords(n: ReplyNoteFlag): string {
  if (n.kind === 'reaction') return 'Reaction, confirm first';
  if (n.kind === 'earlier-answer') return `Earlier answer, see the reply at ${fmtClockPt(n.at)}`;
  return `Answer is in the reply at ${fmtClockPt(n.at)}`;
}

/** APPROVAL 188: a row Select all leaves for her to read. */
export function readFirstWords(r: 'soft' | 'said-more-after'): string {
  return r === 'soft' ? 'Read first' : 'Read first (said more after)';
}

/**
 * The marks a row carries while a reply group is chosen: the blocking one first, then the notes.
 * APPROVAL 188 adds where the answer is and what to read first; 187 the refusal and the changed answer.
 */
export function laterMarks(
  row: Pick<EventRow, 'laterRefusal' | 'answerChanged' | 'replyNote' | 'replyReadFirst'>,
): Array<{ key: string; text: string; pill: string; title: string }> {
  const out: Array<{ key: string; text: string; pill: string; title: string }> = [];
  if (row.replyNote) {
    out.push({
      key: 'reply-note', text: replyNoteWords(row.replyNote), pill: row.replyNote.kind === 'reaction' ? 'pill-warning' : '',
      title: row.replyNote.kind === 'reaction'
        ? 'A phone reaction quotes our own text back; it is never read as an answer.'
        : 'The member answered in another reply; that reply is the one to press.',
    });
  }
  if (row.replyReadFirst) {
    out.push({
      key: 'read-first', text: readFirstWords(row.replyReadFirst), pill: 'pill-warning',
      title: 'Select all leaves this row out. Read the member\'s words, then tick it on its own if it is right.',
    });
  }
  if (row.laterRefusal) {
    out.push({
      key: 'later-refusal', text: laterRefusalWords(row.laterRefusal), pill: 'pill-danger',
      title: 'The member refused after this reply, so Opt In will not approve a kit for it.',
    });
  }
  if (row.answerChanged) {
    out.push({
      key: 'answer-changed', text: answerChangedWords(row.answerChanged), pill: 'pill-warning',
      title: 'The member said 2 and then 1 again. The entry can still be approved; call the member first.',
    });
  }
  return out;
}
